import { ApplicationConfig, importProvidersFrom, isDevMode, LOCALE_ID, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding, withHashLocation, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { NgxPermissionsModule } from 'ngx-permissions';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { headerInterceptor, IGNORED_STATUSES } from './shared/interceptor/header.interceptor';
import { provideHttpClient, withInterceptors, HttpClient, HttpBackend, HttpContext } from '@angular/common/http';
import { catchError, tap, of } from 'rxjs';
import { UserLoginService } from './shared/services/user-login.service';
import localeEn from '@angular/common/locales/en';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import localeItExtra from '@angular/common/locales/extra/it';
import localeEsExtra from '@angular/common/locales/extra/es';
import localePtExtra from '@angular/common/locales/extra/pt';
import { MatPaginatorIntlLocale } from './shared/components/pagination-locale/mat-paginator-intl-locale.service';
import { AppGlobalRippleOptions } from './shared/services/global-ripple-options.service';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { GoogleMapsModule } from '@angular/google-maps';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { provideEchartsCore } from 'ngx-echarts';
import { GroupHttpService } from './shared/services/http/group-http.service';
import { FeatureFlaggedGroupHttpService } from './shared/services/http/feature-flagged-group-http.service';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeIt, 'it', localeItExtra);
registerLocaleData(localeEs, 'es', localeEsExtra);
registerLocaleData(localePt, 'pt', localePtExtra);

export function initializeOAuthSession(httpBackend: HttpBackend, userLoginService: UserLoginService) {
    return () => {
        if (!userLoginService.getIsLoggedIn()) {
            const http = new HttpClient(httpBackend);
            console.log('[AppConfig] No existing cookie IsLoggedIn found. Checking OAuth session (/me)...');
            return http.get<any>('/api/tenant/me', {
                withCredentials: true
            }).pipe(
                tap(() => {
                    console.log('[AppConfig] Set authMode to oauth and cookie IsLoggedIn');
                    sessionStorage.setItem('authMode', 'oauth');
                    userLoginService.setIsLoggedIn(true, false);
                }),
                catchError((error) => {
                    console.error('[AppConfig] Failed to check OAuth session:', error);
                    return of(null);
                })
            );
        }
        return of(null);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideHttpClient(withInterceptors([headerInterceptor])),
        importProvidersFrom(
            MatDatepickerModule,
            MatNativeDateModule,
            MatSnackBarModule,
            MatDialogModule,
            BrowserModule,
            LoadingBarHttpClientModule,
            LoadingBarRouterModule,
            NgxPermissionsModule.forRoot(),
            GoogleMapsModule,
            NgxMatSelectSearchModule
        ),
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        { provide: MatPaginatorIntl, useClass: MatPaginatorIntlLocale },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000 } },
        { provide: LOCALE_ID, useValue: 'en' },
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useExisting: AppGlobalRippleOptions },
        provideAnimations(),
        provideRouter(
            routes,
            withHashLocation(),
            withComponentInputBinding(),
            withRouterConfig({
                onSameUrlNavigation: 'reload',
                paramsInheritanceStrategy: 'always'
            })
        ),
        provideAnimationsAsync(),
        provideHttpClient(),
        provideTransloco({
            config: {
                availableLangs: ['en', 'es', 'it', 'pt'],
                defaultLang: 'en',
                fallbackLang: 'en',
                // Remove this option if your application doesn't support changing language in runtime.
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
                flatten: {
                    aot: !isDevMode()
                }
            },
            loader: TranslocoHttpLoader
        }),
        provideEchartsCore({ echarts: () => import('echarts') }),
        { provide: GroupHttpService, useClass: FeatureFlaggedGroupHttpService },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeOAuthSession,
            deps: [HttpBackend, UserLoginService],
            multi: true
        }
    ]
};
